import React, { createContext, useContext, useEffect } from 'react';
import type {
	PodaniPodaniInstanceDto,
	PodaniPodaniInstanceSeznamDto,
} from '@gov-nx/api';
import {
	mySubmissionsQuery,
	mySubmissionsSearchQuery,
	useAxiosInstance,
	usePoQuery,
} from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useProcessControl } from '@gov-nx/core/hooks';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { FilterParam, Nullable } from '@gov-nx/core/types';
import { FilterSortDirection, Time } from '@gov-nx/core/types';
import { toDateReadable, useFilter } from '@gov-nx/utils/common';
import type { PageCode } from '../../definitions/codes';
import { removeIdFromSubmissionName } from '../../epetice/seznam/utils';
import { getTimePeriod } from '../../upozorneni/utils';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import type {
	MySubmissionsListContext,
	MySubmissionsListControl,
	MySubmissionsListFilter,
} from './context.types';
import { MySubmissionListOrder } from './context.types';
import { useMySubmissionTabs } from './submissionTabs';
import { prepareStatus } from './utils';

const PageMojePodaniSeznamContext =
	createContext<Nullable<MySubmissionsListContext>>(null);

interface SettingsMPSContextProviderProps {
	children: React.ReactNode;
	code: PageCode;
}

const defaultFilter: MySubmissionsListFilter = {
	pocet: 10,
	razeni: MySubmissionListOrder.datumOdeslani,
	razeniSmer: FilterSortDirection.DESC,
	smazano: false,
	start: 0,
	vyrizeno: null,
	lhutaVyrizeni: null,
};

export function MojePodaniSeznamContextProvider({
	children,
	code,
}: SettingsMPSContextProviderProps) {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tn = getLocalizeCurried(code);
	const { axiosInstance } = useAxiosInstance();
	const { setControls, controls } = useProcessControl<MySubmissionsListControl>(
		{
			displayFilter: false,
		}
	);
	const { extraConfig } = useExtraConfigContext();
	const { mySubmissionSelectedTab, setMySubmissionSelectedTab, tabs } =
		useMySubmissionTabs(extraConfig.submissionsRoutes.routesConfig);

	const { filter, setPartialFilter } = useFilter<MySubmissionsListFilter>({
		key: code,
		initialState: defaultFilter,
	});

	const formDefinition = FormInstance({
		code,
		defaultValues: defaultFilter,
	});

	useEffect(() => {
		filter.lhutaVyrizeni &&
			formDefinition.formMethods.setValue(
				'lhutaVyrizeni',
				filter.lhutaVyrizeni
			);
		filter.vyrizeno &&
			formDefinition.formMethods.setValue('vyrizeno', filter.vyrizeno);
		filter.razeni &&
			formDefinition.formMethods.setValue('razeni', filter.razeni);
		filter.razeniSmer &&
			formDefinition.formMethods.setValue('razeniSmer', filter.razeniSmer);
		filter.hledat &&
			formDefinition.formMethods.setValue('hledat', filter.hledat);
	}, [
		filter.hledat,
		filter.lhutaVyrizeni,
		filter.razeni,
		filter.razeniSmer,
		filter.vyrizeno,
		formDefinition.formMethods,
	]);

	const query = usePoQuery<PodaniPodaniInstanceSeznamDto>({
		queryKey: ['my-submissions-filter', filter],
		queryFn: async () => {
			const { params } = prepareSubmitData(filter);

			if (filter.hledat) {
				return mySubmissionsSearchQuery(axiosInstance, filter.hledat, params);
			}

			return mySubmissionsQuery(axiosInstance, params);
		},
		staleTime: Time['10 seconds'],
	});

	const submissions: PodaniPodaniInstanceDto[] = query.data?.seznam ?? [];
	const numberOfSubmissions: number = query.data?.pocet ?? 0;

	const filterParams: FilterParam[] = [
		{
			label: tn('filtr.pole.vyrizeno'),
			value: filter.vyrizeno ? toDateReadable(filter.vyrizeno) : undefined,
			isActive: !!filter.vyrizeno,
			onRemove: () => {
				setPartialFilter({ vyrizeno: undefined, start: 0 });
				formDefinition.formMethods.resetField('vyrizeno');
			},
		},
		{
			label: tn('formular.select.lhuta-vyrizeni'),
			value: filter.lhutaVyrizeni
				? toDateReadable(filter.lhutaVyrizeni)
				: undefined,
			isActive: !!filter.lhutaVyrizeni,
			onRemove: () => {
				setPartialFilter({ lhutaVyrizeni: undefined, start: 0 });
				formDefinition.formMethods.resetField('lhutaVyrizeni');
			},
		},
	];

	const resetFilter = async () => {
		setPartialFilter(defaultFilter);
		setControls({ displayFilter: false });
		formDefinition.formReset();
	};
	const goToPage = async (page: number) => {
		setPartialFilter({ start: (page - 1) * filter.pocet });
	};

	const currentPage = filter.start / filter.pocet + 1;

	const onSubmit = formDefinition.formMethods.handleSubmit(async () => {
		setPartialFilter({ ...formDefinition.formMethods.getValues(), start: 0 });
		setControls({ displayFilter: false });
	});

	return (
		<PageMojePodaniSeznamContext.Provider
			value={
				{
					filterParams,
					submissions: submissions
						.filter((submission) => submission.id != null)
						.map((submission) => ({
							id: submission.id,
							status: prepareStatus(submission),
							name: submission.nazev
								? (removeIdFromSubmissionName(submission.nazev) ?? '')
								: '',
							period: submission.datumOdeslani
								? getTimePeriod(submission.datumOdeslani)
								: undefined,
						})),
					controls,
					query,
					numberOfSubmissions,
					setControls,
					formDefinition,
					resetFilter,
					filter,
					setPartialFilter,
					currentPage,
					onSubmit,
					goToPage,
					tabs,
					code,
					mySubmissionSelectedTab,
					setMySubmissionSelectedTab,
				} as MySubmissionsListContext
			}>
			{children}
		</PageMojePodaniSeznamContext.Provider>
	);
}

export const useMojePodaniSeznamContextInstance =
	(): MySubmissionsListContext =>
		useContext(PageMojePodaniSeznamContext) as MySubmissionsListContext;
